/**
 * useMobileWebKeyboard Hook
 *
 * Handles mobile web virtual keyboard viewport issues.
 * On mobile web browsers (iOS Safari, Chrome Mobile), the virtual keyboard
 * doesn't trigger proper auto-scroll behavior like native apps do.
 *
 * This hook:
 * 1. Detects keyboard appearance via visualViewport API
 * 2. Scrolls the focused input into view when keyboard appears
 * 3. Maintains smooth 60fps scrolling
 *
 * IMPORTANT: This hook is web-only. On native iOS/Android, use
 * react-native-keyboard-controller instead.
 *
 * @see commit 49923f8f - Native keyboard fix (iOS/Android only)
 */
import { useEffect, useCallback, useRef } from "react";
import { isMobileWebBrowser } from "../utils/platform";

interface UseMobileWebKeyboardOptions {
  /**
   * Whether the hook is enabled. Useful for conditional activation.
   * @default true
   */
  enabled?: boolean;
  /**
   * Scroll behavior when scrolling input into view.
   * @default "smooth"
   */
  scrollBehavior?: ScrollBehavior;
  /**
   * Debounce delay for resize events (ms).
   * Prevents excessive scrolling during keyboard animation.
   * @default 150
   */
  debounceDelay?: number;
  /**
   * Minimum viewport height change ratio to detect keyboard (0-1).
   * Default 0.2 means keyboard detected when viewport shrinks by 20%.
   * Works across different device sizes (iPhone SE, iPad, etc.)
   * @default 0.2
   */
  keyboardDetectionRatio?: number;
  /**
   * Dynamic check at scroll execution time to skip scroll.
   * Useful when another component handles scroll (e.g., compact controls).
   * Called right before scrolling to make real-time decision.
   * @returns true to skip scroll, false to proceed
   */
  shouldSkipScroll?: () => boolean;
}

/**
 * Hook that handles mobile web keyboard viewport issues.
 * Automatically scrolls focused inputs into view when the virtual keyboard appears.
 *
 * @example
 * ```tsx
 * function LoginScreen() {
 *   useMobileWebKeyboard();
 *
 *   return (
 *     <ScrollView>
 *       <TextInput placeholder="Email" />
 *       <TextInput placeholder="Password" />
 *     </ScrollView>
 *   );
 * }
 * ```
 */
export function useMobileWebKeyboard(options: UseMobileWebKeyboardOptions = {}) {
  const {
    enabled = true,
    scrollBehavior = "smooth",
    debounceDelay = 150,
    keyboardDetectionRatio = 0.2,
    shouldSkipScroll,
  } = options;

  const isEnabled = enabled && isMobileWebBrowser();
  const initialViewportHeight = useRef<number | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track if we've already scrolled to prevent double-scroll from focusin + viewport handlers
  const hasScrolledForCurrentFocus = useRef(false);

  /**
   * Scroll the currently focused element into view.
   * IMPORTANT: Scrolls to AnalysisSection container (data-analysis-section="true")
   * to keep compact dropdowns (Experience + Context) visible when keyboard appears.
   * Uses scrollIntoView with block: "start" to ensure dropdowns, input, and FAB remain visible.
   * @param markAsScrolled - If true, sets hasScrolledForCurrentFocus to prevent double-scroll
   */
  const scrollFocusedElementIntoView = useCallback((markAsScrolled = true) => {
    // DYNAMIC check at execution time: skip if compact controls are handling scroll
    // This prevents race condition where focusin handler captures enabled=true
    // but keyboard detection later sets isKeyboardVisible=true
    // Note: No isEnabled check here - event listeners are only attached when enabled (see useEffect guard)
    if (shouldSkipScroll?.()) return;

    if (typeof document === "undefined") return;

    // Prevent double-scroll from both focusin and viewport handlers
    if (hasScrolledForCurrentFocus.current) return;

    const activeElement = document.activeElement as HTMLElement | null;
    if (!activeElement) return;

    // Only scroll for input elements (input, textarea, contenteditable)
    const isInputElement =
      activeElement.tagName === "INPUT" ||
      activeElement.tagName === "TEXTAREA" ||
      activeElement.isContentEditable;

    if (!isInputElement) return;

    if (markAsScrolled) {
      hasScrolledForCurrentFocus.current = true;
    }

    // Find the AnalysisSection container using data-analysis-section attribute
    // This container includes: Compact Dropdowns (when keyboard visible) + MessageInput
    let scrollTarget: HTMLElement = activeElement;
    let parent = activeElement.parentElement;

    // Walk up the DOM tree to find element with data-analysis-section="true"
    while (parent && parent !== document.body) {
      // Check for data-analysis-section attribute (explicitly set on AnalysisSection wrapper)
      if (parent.getAttribute('data-analysis-section') === 'true') {
        scrollTarget = parent;
        break;
      }

      parent = parent.parentElement;
    }

    // Fallback: If attribute not found, use heuristic (multiple children + reasonable height)
    if (scrollTarget === activeElement) {
      parent = activeElement.parentElement;
      while (parent && parent !== document.body) {
        const childSections = parent.children.length;
        if (childSections >= 2 && parent.clientHeight > 200) {
          scrollTarget = parent;
          break;
        }
        parent = parent.parentElement;
      }
    }

    // Use scrollIntoView with options for smooth scrolling
    // block: "start" ensures the section (including ContextSelector) is at top of visible area
    scrollTarget.scrollIntoView({
      behavior: scrollBehavior,
      block: "start",
      inline: "nearest",
    });
  }, [shouldSkipScroll, scrollBehavior]);

  /**
   * Handle visualViewport resize events.
   * The viewport shrinks when the keyboard appears on mobile browsers.
   */
  const handleViewportResize = useCallback(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;

    const currentHeight = window.visualViewport.height;

    // Initialize on first call
    if (initialViewportHeight.current === null) {
      initialViewportHeight.current = currentHeight;
      return;
    }

    // Debounce to prevent excessive scrolling during keyboard animation
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      // Keyboard is likely open if viewport height decreased by more than the threshold ratio
      // Using a ratio (default 20%) instead of fixed pixels works across different device sizes:
      // - iPhone SE (568px): 20% = 113px threshold (keyboard ~270px)
      // - iPhone 14 (844px): 20% = 169px threshold (keyboard ~300px)
      // - iPad (1024px): 20% = 205px threshold (keyboard ~300-350px)
      const heightDifference = initialViewportHeight.current! - currentHeight;
      const threshold = initialViewportHeight.current! * keyboardDetectionRatio;
      const keyboardLikelyOpen = heightDifference > threshold;

      if (keyboardLikelyOpen) {
        scrollFocusedElementIntoView();
      }
    }, debounceDelay);
  }, [scrollFocusedElementIntoView, debounceDelay, keyboardDetectionRatio]);

  /**
   * Handle focus events on input elements.
   * Scroll into view after a delay to let keyboard animation start.
   * Uses shared scroll state to prevent double-scroll with viewport handler.
   */
  const handleFocusIn = useCallback(
    (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const isInputElement =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (!isInputElement) return;

      // Reset scroll state on new focus
      hasScrolledForCurrentFocus.current = false;

      // Delay matches debounceDelay to coordinate with viewport handler
      // Both handlers use shared hasScrolledForCurrentFocus to prevent double-scroll
      setTimeout(() => {
        scrollFocusedElementIntoView();
      }, debounceDelay);
    },
    [scrollFocusedElementIntoView, debounceDelay]
  );

  /**
   * Handle blur events to reset scroll tracking state.
   */
  const handleFocusOut = useCallback(() => {
    hasScrolledForCurrentFocus.current = false;
  }, []);

  useEffect(() => {
    if (!isEnabled) return;
    if (typeof window === "undefined") return;

    // Set up visualViewport listener if available
    const visualViewport = window.visualViewport;
    if (visualViewport) {
      // Store initial height
      initialViewportHeight.current = visualViewport.height;

      // Listen to viewport resize (keyboard appear/disappear)
      visualViewport.addEventListener("resize", handleViewportResize);
    }

    // Listen to focusin events for scroll on focus
    document.addEventListener("focusin", handleFocusIn);
    // Listen to focusout to reset scroll tracking state
    document.addEventListener("focusout", handleFocusOut);

    // Cleanup
    return () => {
      if (visualViewport) {
        visualViewport.removeEventListener("resize", handleViewportResize);
      }
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [isEnabled, handleViewportResize, handleFocusIn, handleFocusOut]);

  return {
    /**
     * Whether the mobile web keyboard handling is active.
     */
    isActive: isEnabled,
    /**
     * Manually trigger scroll to focused element.
     * Useful for programmatic focus scenarios.
     */
    scrollToFocused: scrollFocusedElementIntoView,
  };
}

export default useMobileWebKeyboard;
